/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable @typescript-eslint/no-unnecessary-type-conversion */

import { Layout, TextButton, TextLink } from '@oneaudi/unified-web-components';
import React from 'react';
import type { CardButtonsProps, I18nTexts } from '../../../@types/FeatureAppTypes';
import { useTrackingManager } from '../../context/useTrackingManager';
import { matchI18n, useI18n } from '../../i18n';
import { useInitialState } from '../../context/InitialStateContext';
import { useAppContext, useNumberFormatter } from '../../context/Context';
import styled, { useTheme } from 'styled-components';
import type { StockCar } from '../../../@types/MoxxTypes';

const ButtonsWrapper = styled.div<{ discoverIsPrimaryBtn: boolean }>`
  display: flex;
  flex-direction: column;
  padding-bottom: ${({ theme }) => theme.spacing.fixed200};
  gap: ${({ theme }) => theme.spacing.fixed150};
  > :first-child {
    order: ${({ discoverIsPrimaryBtn }) => (discoverIsPrimaryBtn ? 2 : 1)};
  }

  > :nth-child(2) {
    order: ${({ discoverIsPrimaryBtn }) => (discoverIsPrimaryBtn ? 1 : 2)};
  }
`;

const TextLinkWrapper = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.fixed200};
  text-align: center;
  display: flex;
  flex-direction: column;
`;
// eslint-disable-next-line react/display-name
const CardButtons = React.memo(({ carlineGroup, carline, ctaDisplayOptions }: CardButtonsProps) => {
  const availableCarsInStock = carline.availableStock;

  let carsInStock = 0;
  if (availableCarsInStock?.newCars && availableCarsInStock?.newCars?.amount) {
    carsInStock += availableCarsInStock.newCars.amount;
  }
  if (availableCarsInStock?.usedCars && availableCarsInStock.usedCars?.amount) {
    carsInStock += availableCarsInStock.usedCars.amount;
  }

  const newCars = availableCarsInStock?.newCars as StockCar;
  const usedCars = availableCarsInStock?.usedCars as StockCar;

  const { discoverLinkUrl, configureLinkUrl } = carline;

  if (!discoverLinkUrl && !configureLinkUrl && carsInStock === 0) {
    return null;
  }
  const { i18nMessages } = useInitialState();

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { trackCTAClick } = useTrackingManager();
  const i18nButtonTexts: I18nTexts = {
    discoverCTALabel: useI18n('discoverCTALabel'),
    configureCTALabel: useI18n('configureCTALabel'),
  };

  const newCarsMarketText = matchI18n(
    'newAndUsedCarsPopoverCTANewCarsMarketTextWithValue',
    i18nMessages,
    {
      value: useNumberFormatter(newCars ? newCars.amount : 0).toString(),
    },
  );

  const usedCarsMarketText = matchI18n(
    'newAndUsedCarsPopoverCTAUsedCarsMarketTextWithValue',
    i18nMessages,
    {
      value: useNumberFormatter(usedCars ? usedCars.amount : 0).toString(),
    },
  );
  const { isDiscoverPrimaryBtn, showConfigureCTA, selectedFilter } = useAppContext();
  const discoverIsPrimaryBtn = isDiscoverPrimaryBtn ? isDiscoverPrimaryBtn : false;
  const theme = useTheme();
  const fixed150 = theme.spacing.fixed150;

  return (
    <Layout direction="column">
      <ButtonsWrapper discoverIsPrimaryBtn={discoverIsPrimaryBtn}>
        {ctaDisplayOptions.showConfigureCTA && configureLinkUrl && (
          <TextButton
            data-testid="configureButton"
            variant={discoverIsPrimaryBtn && showConfigureCTA ? 'secondary' : 'primary'}
            size="sm"
            href={configureLinkUrl}
            aria-label={matchI18n('configureCTAAriaLabel', i18nMessages, {
              value: carline.name,
            })}
            onClick={() => {
              trackCTAClick(
                configureLinkUrl,
                i18nButtonTexts.configureCTALabel,
                'configure',
                `${carlineGroup.id}~${carline.name}`,
                selectedFilter,
                'button',
              );
            }}
          >
            {i18nButtonTexts.configureCTALabel}
          </TextButton>
        )}

        {discoverLinkUrl && (
          <TextButton
            data-testid="discoverButton"
            variant={
              discoverIsPrimaryBtn || (!discoverIsPrimaryBtn && !showConfigureCTA)
                ? 'primary'
                : 'secondary'
            }
            size="sm"
            aria-label={matchI18n('discoverCTAAriaLabel', i18nMessages, {
              value: carline.name,
            })}
            href={discoverLinkUrl}
            onClick={() => {
              trackCTAClick(
                discoverLinkUrl,
                i18nButtonTexts.discoverCTALabel,
                'discover',
                `${carlineGroup.id}~${carline.name}`,
                selectedFilter,
                'button',
              );
            }}
          >
            {i18nButtonTexts.discoverCTALabel}
          </TextButton>
        )}
      </ButtonsWrapper>

      {(ctaDisplayOptions.showNewCarsCTA || ctaDisplayOptions.showUsedCarsCTA) &&
        carsInStock > 0 && (
          <TextLinkWrapper>
            {ctaDisplayOptions.showNewCarsCTA && newCars && (
              <TextLink
                data-testid="newCarsTextLink"
                {...(newCars.amount > 0
                  ? {
                      href: newCars.url,
                      rel: 'noopener',
                      target: '_self',
                    }
                  : {
                      type: 'button',
                      disabled: true,
                    })}
                margin={[fixed150, 'auto']}
                size="sm"
                variant="standalone"
                onClick={() => {
                  trackCTAClick(
                    newCars.url,
                    matchI18n('newAndUsedCarsPopoverCTANewCarsMarketText', i18nMessages),
                    'vtp new',
                    `${carlineGroup.id}~${carline.name}`,
                    selectedFilter,
                    'text link',
                    newCars.amount,
                  );
                }}
              >
                {newCarsMarketText}
              </TextLink>
            )}
            {ctaDisplayOptions.showUsedCarsCTA && usedCars && (
              <TextLink
                data-testid="usedCarsTextLink"
                {...(usedCars.amount > 0
                  ? {
                      href: usedCars.url,
                      rel: 'noopener',
                      target: '_self',
                    }
                  : {
                      type: 'button',
                      disabled: true,
                    })}
                margin={[fixed150, 'auto']}
                size="sm"
                variant="standalone"
                onClick={() => {
                  trackCTAClick(
                    usedCars.url,
                    matchI18n('newAndUsedCarsPopoverCTAUsedCarsMarketText', i18nMessages),
                    'vtp used',
                    `${carlineGroup.id}~${carline.name}`,
                    selectedFilter,
                    'text link',
                    usedCars.amount,
                  );
                }}
              >
                {usedCarsMarketText}
              </TextLink>
            )}
          </TextLinkWrapper>
        )}
    </Layout>
  );
});

export default CardButtons;
